import { HttpClient } from "@angular/common/http";
import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { IFileUploadDetails } from "@app/directives/_forms/fileUpload/fileUpload.component";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { VTableSelectionType } from "@app/enum/VTableSelectionType";
import { CommserverMigrationStatus, IConfigurableCameraDevice, IPeripheral } from "@app/interfaces/IPeripheral";
import { IReseller } from "@app/main/modules/foundation/_models/IReseller";
import { CommServerRequestState } from "@app/main/modules/foundation/_routes/hardware/_enums/CommServerRequestState";
import { IConfigCameraRequest } from "@app/main/modules/foundation/_routes/rv-lite/camera-config/camera-configuration.models";
import { ICameraConfiguration } from "@app/main/modules/foundation/_routes/rv-lite/config/_models/ICameraConfiguration";
import { CameraConfigurationRequestService } from "@app/main/modules/foundation/_routes/rv-lite/config/_services/cameraConfigurationRequestService";
import { ConfigManagerService } from "@app/main/modules/foundation/_routes/rv-lite/config/_services/config.service";
import { AccountService } from "@app/main/modules/foundation/_services/account.service";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { IAccount } from "@app/services/account.service";
import { CameraCommunicationServerService } from "@app/services/camera/camera-communication-server.service";
import { IDeviceInfo } from "@app/services/cameraCommunicationServer.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { copy } from "angular";
import { combineLatest, forkJoin, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { IPill, VPillsComponent } from "../../../../../../directives/_veri/v-pills/v-pills.component";
import { ResellerAccountMappingService } from "../../../../../../services/resellerAccountMapping.service";
import './commserver-migration.component.scss';

@UntilDestroy()
@Component({
    selector: 'commserver-migration',
    template: require('./commserver-migration.component.html')
})
export class CommserverMigration implements OnInit {
    cameras: IConfigurableCameraDevice[];
    accounts: Map<number, IAccount>;
    accountsList: IAccount[];

    loading: boolean;
    processingSerials: boolean;
    filterTabs: IFilterBarTab<IConfigurableCameraDevice>[];

    tableColumns: IVTableColumn<IConfigurableCameraDevice>[];
    tableRows: IVTableRow[];
    VTableSelectionType = VTableSelectionType;
    selectedRows: IVTableRow[];
    selectedDeviceColumns: IVTableColumn<IConfigurableCameraDevice>[];
    selectedDevices: IConfigurableCameraDevice[] = [];

    csvSerialsList: string[];
    csvRows: IVTableRow[];
    csvDevices: IConfigurableCameraDevice[] = [];
    failedSerials: ICsvSerialFail[] = [];
    failRows: IVTableRow[];
    failedColumns: IVTableColumn<ICsvSerialFail>[];

    allConfigs: ICameraConfiguration[];
    migrationConfigIds: string[];

    showMigrationSideBar: boolean = false;
    showCsvSidebar: boolean = false;
    configRequests: IConfigCameraRequest[];

    resellers: IReseller[] = []

    @ViewChild('confirmApplyTemplate') confirmApplyTemplate: TemplateRef<any>;
    dialog: VDialogRef;

    constructor(
        private readonly http: HttpClient,
        private readonly accountService: AccountService,
        private readonly filterBarService: FilterBarService,
        private readonly vTableService: VTableService,
        private readonly configService: ConfigManagerService,
        private readonly peripheralService: PeripheralService,
        private readonly configRequestService: CameraConfigurationRequestService,
        private readonly translationService: TranslationService,
        private readonly dialogService: DialogService,
        private readonly cameraCommunicationService: CameraCommunicationServerService,
        private readonly resellerAccountMappingService: ResellerAccountMappingService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    onMigrate() {
        this.dialog = this.dialogService.open(this.confirmApplyTemplate, null);
    }

    confirmMigrationAndCloseDialog() {
        const migrationDevices: IApiMigrationDevice[] = this.csvDevices.map(device => ({
            Serial: device.Serial,
            VehicleId: device.vehicleMapping?.vehicle?.Id
        }));

        migrationDevices.forEach(d => this.cameras.find(c => c.Serial === d.Serial).migrationStatus = CommserverMigrationStatus.InProgress);

        this.tableRows = this.vTableService.createTableRows(this.cameras, this.tableColumns);

        this.configService.applyMigration(migrationDevices).pipe(untilDestroyed(this)).subscribe({
            complete: () => {
                this.closeDialog();
                this.showMigrationSideBar = false;
            }
        });
    }

    closeDialog(): void {
        this.dialog.close();
    }

    onSelectedRowsChanged(rows: IVTableRow[]) {
        const selectedRowsCopy = copy(rows);
        this.selectedRows = selectedRowsCopy.map(row => {
            row.cells = row.cells.slice(2, 5);
            return row;
        });

        this.selectedDevices = rows.map(row => row.data);
    }

    toggleActionSidebar(): void {

        this.cleanRows();

        this.showMigrationSideBar = !this.showMigrationSideBar;

        if (this.showMigrationSideBar) {
            this.processingSerials = true;

            this.getDeviceInfos(this.selectedDevices.map(d => d.Serial)).pipe(untilDestroyed(this), tap(deviceInfos => {

                this.selectedDevices.forEach(d => {
                    const deviceInfo = deviceInfos.find(i => i.SerialNumber === d.Serial);

                    const updaterVersion = deviceInfo?.UpdaterVersion;
                    const telematicsVersion = deviceInfo?.TelematicsVersion;

                    if (updaterVersion == null || telematicsVersion == null || updaterVersion == undefined || telematicsVersion == undefined) {
                        this.failedSerials.push({
                            Serial: d.Serial,
                            Reason: this.translationService.t("CommserverMigration.NoAppVersion")
                        })
                    } else if (!this.isVersionHighEnoughToMigrate(updaterVersion, ["3", "17"]) ||
                        !this.isVersionHighEnoughToMigrate(telematicsVersion, ["3", "24"])) {
                        this.failedSerials.push({
                            Serial: d.Serial,
                            Reason: this.translationService.t("CommserverMigration.InsufficientAppVersion")
                        });
                    } else if (d.migrationStatus === CommserverMigrationStatus.Completed) {
                        this.failedSerials.push({
                            Serial: d.Serial,
                            Reason: this.translationService.t("CommserverMigration.AlreadyMigrated")
                        });
                    } else if (d.migrationStatus === CommserverMigrationStatus.InProgress) {
                        this.failedSerials.push({
                            Serial: d.Serial,
                            Reason: this.translationService.t("CommserverMigration.MigrationInProgress")
                        });
                    } else {
                        this.csvDevices.push(d);
                    }
                });

                this.csvRows = this.vTableService.createTableRows(this.csvDevices, this.selectedDeviceColumns);
                this.failRows = this.vTableService.createTableRows(this.failedSerials, this.failedColumns);
                this.processingSerials = false;
            })).subscribe();
        } else {
            this.showCsvSidebar = false;
        }
    }

    private getDeviceInfos(serials: string[]): Observable<IDeviceInfo[]> {
        return this.http.post<IDeviceInfo[]>(`/api/communicationServer/device/infos`, serials)
    }

    private isVersionHighEnoughToMigrate(deviceVersion: string, requiredVersionArray: string[]): boolean {
        const splitVersionStringDevice = deviceVersion.split(".");

        return splitVersionStringDevice[0] === requiredVersionArray[0] && splitVersionStringDevice[1] === requiredVersionArray[1];
    }

    toggleCsvSidebar(): void {
        this.cleanRows();

        this.showCsvSidebar = !this.showCsvSidebar;
        this.showMigrationSideBar = !this.showMigrationSideBar;
    }

    cleanRows(): void {
        this.csvRows = [];
        this.failRows = [];
        this.csvSerialsList = [];
        this.csvDevices = [];
        this.failedSerials = [];
    }

    filterCameraByAccount(camera: IConfigurableCameraDevice, account: IAccount): boolean {
        return camera.vehicleMapping?.vehicle?.AccountId === account.Id;
    }

    accountSearchPredicate(account: IAccount, searchText: string): boolean {
        return account.CompanyName.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    filterCameraByReseller(camera: IConfigurableCameraDevice, reseller: IReseller): boolean {
        return camera.resellerId === reseller.Id;
    }

    resellerSearchPredicate(reseller: IReseller, searchText: string): boolean {
        return reseller.Description.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    ingressFile(file: IFileUploadDetails): void {
        this.csvRows = [];
        this.failRows = [];

        const fileReader = new FileReader();

        fileReader.onload = () => {
            const fileContent = fileReader.result as string;

            this.csvSerialsList = fileContent.split(',').map(serial => serial.trim());

            this.getDeviceInfos(this.csvSerialsList).pipe(untilDestroyed(this), tap(rtlDeviceInfos => {
                this.csvSerialsList.forEach(serial => {
                    const matchingDevice = this.cameras?.find(c => c.Serial == serial);
                    const matchingRtlInfo = rtlDeviceInfos.find(i => i.SerialNumber == serial);

                    const updaterVersion = matchingRtlInfo?.UpdaterVersion;
                    const telematicsVersion = matchingRtlInfo?.TelematicsVersion;

                    if (!matchingDevice) {
                        this.failedSerials.push({ Serial: serial, Reason: this.translationService.t("CommserverMigration.DoesNotExist") });
                    } else if (matchingDevice.migrationStatus === CommserverMigrationStatus.Completed) {
                        this.failedSerials.push({ Serial: serial, Reason: this.translationService.t("CommserverMigration.AlreadyMigrated") });
                    } else if (matchingDevice.migrationStatus === CommserverMigrationStatus.InProgress) {
                        this.failedSerials.push({ Serial: serial, Reason: this.translationService.t("CommserverMigration.MigrationInProgress") });
                    } else if (updaterVersion == null || telematicsVersion == null || updaterVersion == undefined || telematicsVersion == undefined) {
                        this.failedSerials.push({
                            Serial: serial,
                            Reason: this.translationService.t("CommserverMigration.NoAppVersion")
                        })
                    } else if (!this.isVersionHighEnoughToMigrate(updaterVersion, ["3", "17"]) ||
                        !this.isVersionHighEnoughToMigrate(telematicsVersion, ["3", "24"])) {
                        this.failedSerials.push({
                            Serial: serial,
                            Reason: this.translationService.t("CommserverMigration.InsufficientAppVersion")
                        });
                    } else {
                        this.csvDevices.push(matchingDevice);
                    }
                });

                this.csvRows = this.vTableService.createTableRows(this.csvDevices, this.selectedDeviceColumns);
                this.failRows = this.vTableService.createTableRows(this.failedSerials, this.failedColumns);
            })).subscribe();


        };

        fileReader.readAsText(file.file);
    }

    private loadData(): void {
        this.loading = true;
        this.createTabs();
        this.createTable();

        combineLatest([
            this.loadAccounts(),
            this.loadCameras()
        ]).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: () => {
                this.cameras.forEach(camera => {
                    camera.resellerId = this.accountsList?.find(acc => acc.Id == camera.vehicleMapping?.vehicle?.AccountId)?.ResellerId;
                });

                this.getConfigsAndRequests();
            }
        });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: false }),
                createCellFunction: (camera) => this.vTableService.createStatusCell(this.setStatusSymbol(camera.migrationStatus))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CommserverMigration.Migration", width: 150 }),
                createCellFunction: (camera) => this.vTableService.createEnumCell(CommserverMigrationStatus, "CommserverMigrationStatus", camera.migrationStatus)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial", width: 150
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.Serial)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (camera) => this.vTableService.createVehicleRegCell(camera.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Name", minTableWidth: 500 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraConfiguration.CurrentConfig" }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.currentConfig, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", width: 250, minTableWidth: 450 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getCustomerAccountName(camera))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationCustomers.ParentCompany", minTableWidth: 650 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera, {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: this.getCustomerResellerPills(camera)
                    }
                })
            },
        ]

        this.selectedDeviceColumns = copy(this.tableColumns.slice(2, 5));

        this.failedColumns = [
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial", width: 150
                }),
                createCellFunction: (failedModel) => this.vTableService.createTableCell(failedModel.Serial)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reason", minTableWidth: 500 }),
                createCellFunction: (failedModel) => this.vTableService.createTableCell(failedModel.Reason)
            }
        ]
    }

    private setStatusSymbol(commserverMigrationStatus: CommserverMigrationStatus): VTableRowStatus {
        switch (commserverMigrationStatus) {
            case CommserverMigrationStatus.Completed:
                return VTableRowStatus.Success;
            case CommserverMigrationStatus.InProgress:
                return VTableRowStatus.Pending;
            case CommserverMigrationStatus.Required:
                return VTableRowStatus.Warning;
        }
    }

    private loadAccounts(): Observable<any> {
        return combineLatest([
            this.accountService.getCustomerAccounts(),
            this.accountService.getResellers(),
            this.resellerAccountMappingService.getMappings()
        ]).pipe(
            tap(([accounts, resellers, mappings]) => {
                accounts.forEach(account => {
                    this.resellers = resellers;

                    account.ResellerIds = mappings.filter(mapping => mapping.AccountId === account.Id && !mapping.EndTime).map(mapping => mapping.ResellerId);

                    this.createResellerPills(account);
                });

                this.accounts = new Map(accounts.map(account => [account.Id, account]));
                this.accountsList = accounts;
                
            })
        );
    }

    private createResellerPills(account: IAccount): void {
        account.resellerPills = [];

        const resellerPillsExportData: string[] = [];
        let extraCount = 0;

        account.ResellerIds.forEach((resellerId, index) => {
            const reseller = this.resellers.find(r => r.Id === resellerId);

            resellerPillsExportData.push(reseller.Description);
            if (index < 1) {
                account.resellerPills.push({
                    label: reseller?.Description,
                    hovering: false,
                    data: reseller,
                    tooltip: reseller?.Description
                });
            } else {
                extraCount++;
            }
        });

        if (extraCount > 0) {
            account.resellerPills.push({
                label: `+${extraCount}`,
                hovering: true,
                tooltip: resellerPillsExportData.slice(1).join(", ")
            });
        }
    }

    private loadCameras(): Observable<IConfigurableCameraDevice[]> {
        return this.peripheralService.getAllConfigurableCameras().pipe(
            tap(cameras => {
                this.cameras = cameras;
            })
        );
    }

    private setConfigNameString(): void {
        this.cameras.forEach(c => {
            c.currentConfig = this.allConfigs?.find(config => config.Id == c.currentConfig)?.Name ?? this.translationService.t("Common.Unknown");
        });
    }

    private getConfigsAndRequests(): void {
        combineLatest([
            this.configService.getConfigs(),
            this.configService.getMigrationConfigIds(),
            this.configRequestService.getConfigurationRequests({
                RequestStates: [CommServerRequestState.Pending, CommServerRequestState.Requested]
            })
        ]).pipe(untilDestroyed(this),
            tap(([configs, migrationConfigIds, pendingConfigRequests]) => {
                this.allConfigs = configs;
                this.migrationConfigIds = migrationConfigIds;
                this.configRequests = pendingConfigRequests.filter(r =>
                    this.migrationConfigIds.some(id => id == r.ConfigurationId)
                );

                this.cameras.filter(c =>
                    c.migrationStatus === CommserverMigrationStatus.Required &&
                    this.configRequests?.some(r => r.Serial == c.Serial)
                ).forEach(m => {
                    m.migrationStatus = CommserverMigrationStatus.InProgress
                });

                this.setConfigNameString();
                this.createTabs();
                this.loading = false;
                this.tableRows = this.vTableService.createTableRows(this.cameras, this.tableColumns);
            })

        ).subscribe();
    }

    private getCustomerAccountName(peripheral: IPeripheral): string {
        if (!this.accounts?.size ||
            !peripheral?.vehicleMapping?.vehicle ||
            !this.accounts?.has(peripheral.vehicleMapping.vehicle.AccountId)) {
            return;
        }

        return this.accounts.get(peripheral.vehicleMapping.vehicle.AccountId).CompanyName;
    }

    private getCustomerResellerPills(peripheral: IPeripheral): IPill<IReseller>[] {
        if (!this.accounts?.size ||
            !peripheral?.vehicleMapping?.vehicle ||
            !this.accounts?.has(peripheral.vehicleMapping.vehicle.AccountId)) {
            return;
        }

        return this.accounts.get(peripheral.vehicleMapping.vehicle.AccountId).resellerPills;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("Common.All", () => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("CameraConfiguration.Requried", (device) => device?.migrationStatus === CommserverMigrationStatus.Required));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("CameraConfiguration.Pending", (device) => device?.migrationStatus === CommserverMigrationStatus.InProgress));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("CameraConfiguration.Completed", (device) => device?.migrationStatus === CommserverMigrationStatus.Completed));
    }
}

export interface IApiMigrationDevice {
    Serial: string;
    VehicleId?: number
}

export interface ICsvSerialFail {
    Serial: string;
    Reason: string;
}
